/**
 * clnfSlider: a small, dependency-free stand-in for the parts of Slick 1.6 the theme used.
 *
 * It builds the same DOM and classes as Slick (slick-initialized, slick-list, slick-track,
 * slick-slide, slick-arrow / slick-prev / slick-next, slick-dots, slick-active, slick-current,
 * slick-disabled, slick-dotted), so the inlined slick.min.css in layout/theme.liquid and the sections' own
 * .slick-* styles keep working unchanged.
 *
 * Supported options (Slick names and defaults): slidesToShow, slidesToScroll, infinite, arrows,
 * dots, prevArrow / nextArrow (HTML strings), autoplay, autoplaySpeed, pauseOnHover, speed,
 * cssEase, swipeToSlide, responsive ([{ breakpoint, settings }], desktop-first: a breakpoint
 * applies while window.innerWidth < breakpoint). "infinite" rewinds to the start instead of
 * cloning slides.
 *
 * @example
 *   window.clnfSlider(document.querySelector('.video-row'), { slidesToShow: 3, arrows: true });
 */
(function () {
  'use strict';

  var DEFAULTS = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    arrows: true,
    dots: false,
    prevArrow: '<button class="slick-prev" aria-label="Previous" type="button">Previous</button>',
    nextArrow: '<button class="slick-next" aria-label="Next" type="button">Next</button>',
    autoplay: false,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    speed: 300,
    cssEase: 'ease',
    swipeToSlide: false,
    responsive: null,
  };

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var key in src) {
        if (Object.prototype.hasOwnProperty.call(src, key)) target[key] = src[key];
      }
    }
    return target;
  }

  function fromHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  function Slider(el, options) {
    this.el = el;
    this.base = assign({}, DEFAULTS, options);
    this.index = 0;
    this.slides = Array.prototype.slice.call(el.children);
    this.build();
  }

  Slider.prototype.build = function () {
    var el = this.el;
    this.list = document.createElement('div');
    this.list.className = 'slick-list draggable';
    this.track = document.createElement('div');
    this.track.className = 'slick-track';
    this.track.style.opacity = '1';
    this.slides.forEach(function (slide, i) {
      slide.classList.add('slick-slide');
      slide.setAttribute('data-slick-index', i);
      this.track.appendChild(slide);
    }, this);
    this.list.appendChild(this.track);
    el.appendChild(this.list);
    el.classList.add('slick-initialized', 'slick-slider');

    this.bindSwipe();
    if (this.base.pauseOnHover) {
      el.addEventListener('mouseenter', this.pause.bind(this));
      el.addEventListener('mouseleave', this.play.bind(this));
    }

    var self = this;
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.applySettings();
      }, 100);
    });
    if (window.ResizeObserver) {
      var lastWidth = 0;
      new ResizeObserver(function () {
        var w = self.list.clientWidth;
        if (w !== lastWidth) {
          lastWidth = w;
          self.layout(false);
        }
      }).observe(this.list);
    }

    this.applySettings();
  };

  // Resolves the active responsive settings and rebuilds arrows / dots when they change.
  Slider.prototype.applySettings = function () {
    var width = window.innerWidth;
    var active = null;
    (this.base.responsive || []).forEach(function (bp) {
      if (width < bp.breakpoint && (!active || bp.breakpoint < active.breakpoint)) active = bp;
    });
    var key = active ? active.breakpoint : 'base';
    if (key === this.settingsKey) {
      this.layout(false);
      return;
    }
    this.settingsKey = key;
    this.opts = assign({}, this.base, active && active.settings);
    this.opts.slidesToShow = Math.max(1, Math.min(this.opts.slidesToShow, this.slides.length));
    this.buildControls();
    this.goTo(Math.min(this.index, this.maxIndex()), false);
    this.play();
  };

  Slider.prototype.maxIndex = function () {
    return Math.max(0, this.slides.length - this.opts.slidesToShow);
  };

  Slider.prototype.buildControls = function () {
    var self = this;
    var o = this.opts;
    [this.prevBtn, this.nextBtn, this.dots].forEach(function (node) {
      if (node && node.parentNode) node.parentNode.removeChild(node);
    });
    this.prevBtn = this.nextBtn = this.dots = null;
    var scrollable = this.slides.length > o.slidesToShow;

    if (o.arrows && scrollable) {
      this.prevBtn = fromHtml(o.prevArrow);
      this.nextBtn = fromHtml(o.nextArrow);
      this.prevBtn.classList.add('slick-arrow');
      this.nextBtn.classList.add('slick-arrow');
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.prev();
      });
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.next();
      });
      this.el.insertBefore(this.prevBtn, this.list);
      this.el.appendChild(this.nextBtn);
    }

    this.el.classList.toggle('slick-dotted', !!(o.dots && scrollable));
    if (o.dots && scrollable) {
      this.dots = document.createElement('ul');
      this.dots.className = 'slick-dots';
      this.dots.setAttribute('role', 'tablist');
      var pages = this.pageCount();
      for (var i = 0; i < pages; i++) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = String(i + 1);
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.addEventListener('click', this.goTo.bind(this, i * o.slidesToScroll, true, true));
        li.appendChild(btn);
        this.dots.appendChild(li);
      }
      this.el.appendChild(this.dots);
    }
  };

  Slider.prototype.pageCount = function () {
    var o = this.opts;
    var n = this.slides.length;
    return o.infinite ? Math.ceil(n / o.slidesToScroll) : Math.ceil((n - o.slidesToShow) / o.slidesToScroll) + 1;
  };

  // Sizes slides like Slick: list width / slidesToShow, minus each slide's horizontal margins.
  Slider.prototype.layout = function (animate) {
    var o = this.opts;
    // Hidden (0-wide) lists are laid out when the ResizeObserver sees them get a width.
    if (!o || !this.list.clientWidth) return;
    this.slideWidth = this.list.clientWidth / o.slidesToShow;
    var cs = window.getComputedStyle(this.slides[0]);
    var gutter = parseFloat(cs.marginLeft) + parseFloat(cs.marginRight);
    if (cs.boxSizing !== 'border-box') {
      gutter +=
        parseFloat(cs.paddingLeft) +
        parseFloat(cs.paddingRight) +
        parseFloat(cs.borderLeftWidth) +
        parseFloat(cs.borderRightWidth);
    }
    var slideWidth = this.slideWidth;
    // The slider owns slide widths: stray min/max-width rules would make slides overflow the
    // track and wrap (e.g. watch-more's .drop-colunm min-width: 300px on phones).
    this.slides.forEach(function (slide) {
      slide.style.width = slideWidth - gutter + 'px';
      slide.style.minWidth = '0';
      slide.style.maxWidth = 'none';
    });
    this.track.style.width = Math.ceil(slideWidth * this.slides.length) + 'px';
    this.translate(-this.index * slideWidth, animate);
  };

  Slider.prototype.translate = function (x, animate) {
    this.track.style.transition = animate ? 'transform ' + this.opts.speed + 'ms ' + this.opts.cssEase : 'none';
    this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
  };

  Slider.prototype.goTo = function (target, animate, fromUser) {
    var o = this.opts;
    var max = this.maxIndex();
    if (target > max) target = o.infinite && this.index >= max ? 0 : max;
    if (target < 0) target = o.infinite && this.index <= 0 ? max : 0;
    this.index = target;
    this.layout(animate !== false);

    var start = target;
    var end = target + o.slidesToShow;
    this.slides.forEach(function (slide, i) {
      var visible = i >= start && i < end;
      slide.classList.toggle('slick-active', visible);
      slide.classList.toggle('slick-current', i === start);
    });
    if (this.prevBtn && !o.infinite) this.prevBtn.classList.toggle('slick-disabled', target <= 0);
    if (this.nextBtn && !o.infinite) this.nextBtn.classList.toggle('slick-disabled', target >= max);
    if (this.dots) {
      var page = Math.min(Math.ceil(target / o.slidesToScroll), this.dots.children.length - 1);
      Array.prototype.forEach.call(this.dots.children, function (li, i) {
        li.classList.toggle('slick-active', i === page);
      });
    }
    if (fromUser) this.play();
  };

  Slider.prototype.next = function () {
    this.goTo(this.index + this.opts.slidesToScroll, true, true);
  };

  Slider.prototype.prev = function () {
    this.goTo(this.index - this.opts.slidesToScroll, true, true);
  };

  Slider.prototype.play = function () {
    this.pause();
    if (!this.opts || !this.opts.autoplay || this.slides.length <= this.opts.slidesToShow) return;
    var self = this;
    this.timer = setInterval(function () {
      if (!document.hidden) self.goTo(self.index + self.opts.slidesToScroll, true);
    }, this.opts.autoplaySpeed);
  };

  Slider.prototype.pause = function () {
    clearInterval(this.timer);
  };

  // Pointer drag: follows the finger, then moves by one page (or by the dragged slide count
  // with swipeToSlide) once the drag passes a fifth of the list width, like Slick's touchThreshold.
  Slider.prototype.bindSwipe = function () {
    var self = this;
    var list = this.list;
    var startX = 0;
    var startY = 0;
    var dx = 0;
    var dragging = false;
    var horizontal = null;
    var moved = false;

    list.style.touchAction = 'pan-y';
    list.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (self.slides.length <= self.opts.slidesToShow) return;
      dragging = true;
      horizontal = null;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      self.pause();
    });
    list.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      dx = e.clientX - startX;
      if (horizontal === null && (Math.abs(dx) > 6 || Math.abs(e.clientY - startY) > 6)) {
        horizontal = Math.abs(dx) > Math.abs(e.clientY - startY);
        if (horizontal && list.setPointerCapture) list.setPointerCapture(e.pointerId);
      }
      if (!horizontal) return;
      moved = true;
      self.translate(-self.index * self.slideWidth + dx, false);
    });
    function end() {
      if (!dragging) return;
      dragging = false;
      if (!horizontal) {
        self.play();
        return;
      }
      if (Math.abs(dx) < list.clientWidth / 5) {
        self.goTo(self.index, true, true);
        return;
      }
      var step = self.opts.swipeToSlide ? Math.max(1, Math.round(Math.abs(dx) / self.slideWidth)) : self.opts.slidesToScroll;
      self.goTo(self.index + (dx < 0 ? step : -step), true, true);
    }
    list.addEventListener('pointerup', end);
    list.addEventListener('pointercancel', end);
    // A drag must not also follow the link it started on.
    list.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );
    list.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  };

  window.clnfSlider = function (el, options) {
    if (!el || el.classList.contains('slick-initialized') || !el.children.length) return null;
    el.clnfSlider = new Slider(el, options);
    return el.clnfSlider;
  };
})();
